import { Button, ButtonVariant } from '@hourly-hds/components';
import { Alert as AlertIcon, Bank as BankIcon } from '@hourly-hds/icons/colored';
import { useState } from 'react';
import Plaid, {
  CustomPlaidLinkError,
  CustomPlaidLinkOnSuccessMetadata,
} from '@shared/components/Plaid';
import { useBankAccount } from '@shared/data/hooks/useBankAccount';
import { TabContentSpinner } from '../../../_shared/components/TabContentSpinner';
import styles from './BankAccount.module.css';
import {
  BankAccountReplacementSuccessDialog,
  ReplaceBankAccountConfirmationDialog,
} from './components/BankAccountDialog';
import { useGetBankAccount } from './data/hooks/useGetBankAccount';

export default function BankAccount({ partnerId }: { partnerId: string }) {
  const { bankAccount, isSettled, fetchBankAccount } = useGetBankAccount(partnerId);
  const {
    token,
    isAwaitingMicrodepositVerification,
    isBankAccountRequestLoading,
    loadSameDayMicrodepositToken,
    startSameDayMicrodeposit,
    addBankAccount,
    deleteBankAccountValidationToken,
    cleanToken,
    getBankAccountToken,
    cleanSameDayMicrodepositLink,
  } = useBankAccount(partnerId!, {
    requestSameDayMicrodepositOnMount: true,
  });

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);

  if (!isSettled || isBankAccountRequestLoading) {
    return <TabContentSpinner />;
  }

  const handleSuccessAddBankAccount = (
    publicToken: string,
    metadata: CustomPlaidLinkOnSuccessMetadata
  ) => {
    if (metadata.account.verification_status === 'pending_manual_verification') {
      startSameDayMicrodeposit(publicToken, {
        onSuccess: () => {
          cleanToken();
        },
      });
      return;
    }

    addBankAccount(publicToken, {
      onSuccess: () => {
        setIsSuccessDialogOpen(true);
        fetchBankAccount();
        cleanToken();
        cleanSameDayMicrodepositLink();
      },
    });
  };

  const handleExitPlaid = async (exitError: CustomPlaidLinkError | null) => {
    if (exitError?.error_code === 'TOO_MANY_VERIFICATION_ATTEMPTS') {
      await deleteBankAccountValidationToken();
      return;
    }

    cleanToken();
  };

  return (
    <div>
      <div className={styles.header}>
        <p className={styles.title}>Bank Account</p>
        {!isAwaitingMicrodepositVerification && (
          <Button
            label="Replace"
            variant={ButtonVariant.Primary}
            onClick={() => setIsConfirmationDialogOpen(true)}
            disabled={isBankAccountRequestLoading || isConfirmationDialogOpen}
          />
        )}
      </div>

      <div className={styles.content}>
        <div className={styles.bankInfo}>
          {isAwaitingMicrodepositVerification ? (
            <>
              <AlertIcon />
              <div className={styles.bankInfoText}>Bank account verification in progress</div>
            </>
          ) : (
            <>
              <BankIcon />
              <div className={styles.bankInfoText}>{bankAccount?.displayInfo}</div>
            </>
          )}
        </div>

        {isAwaitingMicrodepositVerification && (
          <Button
            label="Verify Account"
            onClick={loadSameDayMicrodepositToken}
            disabled={isBankAccountRequestLoading}
          />
        )}
      </div>

      {token && (
        <Plaid
          token={token}
          onSuccess={(publicToken, metadata) => handleSuccessAddBankAccount(publicToken, metadata)}
          onExit={handleExitPlaid}
        />
      )}

      <ReplaceBankAccountConfirmationDialog
        isOpen={isConfirmationDialogOpen}
        onConfirm={getBankAccountToken}
        onClose={() => setIsConfirmationDialogOpen(false)}
      />

      <BankAccountReplacementSuccessDialog
        isOpen={isSuccessDialogOpen}
        onClose={() => setIsSuccessDialogOpen(false)}
      />
    </div>
  );
}
